import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { Alert, Card, CardBody } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RiFilter3Fill } from 'react-icons/ri';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  getPaginationRowModel,
  SortingState,
} from '@tanstack/react-table';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useReadContracts } from 'wagmi';

import DebouncedInput from '@/components/DebounceInput';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import { fetchUsersRest } from '@/services/users/users-rest';
import SkeletonRow from '@/components/data-table/SkeletonRow';
import Paginator from '@/pages/common/paginator';
import { UserInfo } from '@/services/users/user-types';
import { formatUnits } from '@ethersproject/units';
import { abi as usdtABI } from '@/common/abis/usdt';
import { abi as phnmABI } from '@/common/abis/phnm';
import { config } from '@repo/common-types';
import SortableTableHeader from '@/components/data-table/SortableTableHeader';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';

import { initialState, queryReducer } from '../user-query-reducer';
import UserEditRole from './UsersEditRole';
import { createColumns } from '../user.columns';
import UsersFilter from './UserFilters';

const UsersTable: React.FC = () => {
  const navigate = useNavigate();
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [queryParams, dispatch] = useReducer(queryReducer, initialState);
  const [editItem, setEditItem] = useState<{ user: UserInfo; role: string }>();
  const [modal, setModal] = useState<boolean>(false);

  const { sorting, ...rest } = queryParams;

  const { data, isLoading, error } = useQuery({
    queryKey: ['userData', queryParams],
    queryFn: () =>
      fetchUsersRest({
        ...rest,
        sorts: convertSortingToApiFormat(sorting),
      }),
    staleTime: 60000,
  });

  const { data: userUsdtBalances, isLoading: isLoadingUserUsdtBalances } = useReadContracts({
    contracts: (data?.users || []).reduce((acc: any, curr: any) => {
      if (curr.smartWalletAddress) {
        acc.push({
          address: config.contracts.usdt,
          abi: usdtABI,
          functionName: 'balanceOf',
          args: [curr?.smartWalletAddress],
        });
      }
      return acc;
    }, []),
  });

  const { data: userPHNMBalances, isLoading: isLoadingUserPHNMBalances } = useReadContracts({
    contracts: (data?.users || []).reduce((acc: any, curr: any) => {
      if (curr.smartWalletAddress) {
        acc.push({
          address: config.contracts.phnm,
          abi: phnmABI,
          functionName: 'balanceOf',
          args: [curr?.smartWalletAddress],
        });
      }
      return acc;
    }, []),
  });

  const userListWithBalances = useMemo(() => {
    if (!isLoadingUserPHNMBalances && !isLoadingUserUsdtBalances) {
      return (data?.users || []).map((user, index) => {
        return {
          ...user,
          usdtBalance: Number(
            formatUnits(userUsdtBalances?.[index]?.result || (0 as any), config.blockchain.usdtDecimals),
          ).toFixed(2),
          phnmBalance: Number(
            formatUnits(userPHNMBalances?.[index]?.result || (0 as any), config.blockchain.usdtDecimals),
          ).toFixed(2),
        };
      });
    }

    return [];
  }, [data?.users, userUsdtBalances, userPHNMBalances, isLoadingUserPHNMBalances, isLoadingUserUsdtBalances]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      dispatch({ type: 'SET_SORTING', payload: newSorting });
    },
    [sorting],
  );

  const handleRoleChange = (user: UserInfo, role: string) => {
    setEditItem({ user, role });
    setModal(true);
  };

  const columns = useMemo(() => createColumns(navigate, handleRoleChange), [navigate]);

  const tableInstance = useReactTable({
    data: userListWithBalances,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    state: {
      sorting: sorting,
    },
  });

  const handleSearch = useCallback((value: string | number) => {
    setIsDebouncing(true);
    dispatch({ type: 'SET_SEARCH', payload: value.toString() });
    setIsDebouncing(false);
  }, []);

  const handlePaginationChange = useCallback((skip: number, take: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { skip, take } });
  }, []);

  const handleModal = useCallback(() => {
    setModal(!modal);
    setEditItem(undefined);
  }, [modal]);

  const modalCallback = (success: boolean, message: string) => {
    const toastType = success ? toast.success : toast.warning;
    toastType(message, {
      position: 'bottom-right',
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: 'light',
      transition: Bounce,
    });
  };

  if (error) {
    return <Alert color="danger">Error when fetching user data</Alert>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="w-100 d-flex justify-content-between align-items-center gap-2 py-2">
        <div className="d-flex align-items-center justify-content-start gap-2">
          <DebouncedInput
            className="form-control w-auto"
            placeholder="Search users..."
            onChange={handleSearch}
            value={queryParams.filter?.search || ''}
            isLoading={isDebouncing}
          />
          <Button
            onClick={() => setShowFilterModal(true)}
            className="btn btn-outline d-flex align-items-center justify-content-center gap-2"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
          >
            <RiFilter3Fill size={20} />
            Filter
          </Button>
          {Object.values(queryParams.filter || {}).some(Boolean) && (
            <Button
              size={ButtonSizes.sm}
              variant={ButtonVariants.primary}
              onClick={() => dispatch({ type: 'RESET_FILTERS' })}
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardBody className="overflow-auto">
          <table className="table-hover table-centered table-nowrap users-table mb-0 table align-middle">
            <thead className="thead-light users-table__header">
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader key={header.id} header={header} />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: queryParams.take || 25 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-${index}`} columns={columns} />
                  ))
                : tableInstance.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Paginator
        take={queryParams.take || 25}
        skip={queryParams.skip || 0}
        showing={data?.users.length || 0}
        totalCount={data?.totalCount || 0}
        setTake={(take) => handlePaginationChange(queryParams.skip || 0, take)}
        setSkip={(skip) => handlePaginationChange(skip, queryParams.take || 25)}
      />

      {showFilterModal && (
        <UsersFilter
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={(filters) => {
            dispatch({ type: 'SET_FILTERS', payload: filters });
            setShowFilterModal(false);
          }}
          currentFilters={queryParams.filter}
        />
      )}

      {editItem && (
        <UserEditRole
          show={modal}
          userId={editItem.user.id}
          user={editItem?.user}
          role={editItem?.role}
          refetch={() => {}}
          modalCallback={modalCallback}
          handleClose={handleModal}
        />
      )}
    </div>
  );
};

export default UsersTable;
